// components/PricingCard.js
import { FaCheck, FaWhatsapp, FaCreditCard, FaArrowRight } from 'react-icons/fa';
import { useState } from 'react';
import PlanoAnualDetails from './PlanoAnualDetails';

const PricingCard = () => {
  const [showPlanoAnual, setShowPlanoAnual] = useState(false);
  const [isAnnual, setIsAnnual] = useState(false);

  // Valores do cálculo
  const monthlyPrice = 149.90;
  const annualTotal = monthlyPrice * 12;
  const discount = annualTotal * 0.10;
  const finalAnnualPrice = annualTotal - discount;
  const effectiveMonthlyPrice = finalAnnualPrice / 12;

  // Número do WhatsApp formatado
  const whatsappUrl = "https://wa.me/5582996992182?text=Olá! Gostaria de saber mais sobre o plano App Nativo Premium.";
  const mercadoPagoUrl = "https://www.mercadopago.com.br/checkout/v1/redirect?pref_id=147885044-a4a85dac-5f01-4141-8eda-6e58727c833e";

  const benefits = [
    { feature: "App Android e iOS", desc: "Nativo e otimizado" },
    { feature: "Publicação nas Lojas", desc: "Google Play e App Store" },
    { feature: "Suporte Prioritário", desc: "24/7 via WhatsApp" },
    { feature: "Atualizações Gratuitas", desc: "Sempre na versão atual" },
    { feature: "Backup Automático", desc: "Dados protegidos" },
    { feature: "Analytics Integrado", desc: "Métricas em tempo real" },
    { feature: "Push Ilimitado", desc: "Engaje seus usuários" },
    { feature: "Segurança SSL", desc: "Proteção premium" }
  ];

  return (
    <section className="py-24 bg-white relative">
      {/* Background Pattern - Subtle diagonal stripes */}
      <div 
        className="absolute inset-0 pointer-events-none opacity-[0.03]"
        style={{
          backgroundImage: `repeating-linear-gradient(45deg, #000 0, #000 1px, transparent 0, transparent 50%)`,
          backgroundSize: '10px 10px'
        }}
      ></div>

      <div className="container mx-auto px-4 sm:px-6 relative z-10">
        
        {/* Header Section */}
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 tracking-tight mb-4">
            Investimento
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-lg font-light leading-relaxed">
            Tenha seu aplicativo publicado nas lojas com uma assinatura que cabe no seu bolso.
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          {/* Card Principal */}
          <div className="bg-white rounded-3xl border border-gray-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] overflow-hidden transition-all duration-300">
            
            {/* Topo / Preço */}
            <div className="p-8 md:p-12 border-b border-purple-100 flex flex-col md:flex-row items-center justify-between gap-8">
              
              <div className="text-center md:text-left flex-1">
                <div className="inline-flex items-center px-3 py-1 bg-gray-50 border border-gray-200 rounded-full text-xs font-semibold text-gray-700 tracking-wide mb-4">
                  <span className="w-2 h-2 rounded-full bg-black mr-2 animate-pulse"></span>
                  Um plano que cabe no seu bolso.
                </div>
                <h3 className="text-3xl font-bold text-gray-900 tracking-tight mb-2">
                  App Nativo Completo
                </h3>
                <p className="text-gray-500 font-light text-sm">
                  Tudo que você precisa para Android e iOS. Sem taxa de setup.
                </p>
              </div>

              <div className="text-center md:text-right flex-shrink-0">
                <div className="flex items-center justify-center md:justify-end gap-3 mb-2">
                  {/* Toggle Anual/Mensal */}
                  <div className="bg-gray-50 border border-gray-100 p-1 rounded-full flex text-xs font-medium">
                    <button 
                      onClick={() => setIsAnnual(false)}
                      className={`px-3 py-1 rounded-full transition-all ${!isAnnual ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-900'}`}
                    >
                      Mensal
                    </button>
                    <button 
                      onClick={() => setIsAnnual(true)}
                      className={`px-3 py-1 rounded-full transition-all flex items-center gap-1 ${isAnnual ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-900'}`}
                    >
                      Anual <span className="text-green-600 font-bold">-10%</span>
                    </button>
                  </div>
                </div>

                <div className="flex items-baseline justify-center md:justify-end gap-1 mt-4">
                  <span className="text-gray-400 text-lg">R$</span>
                  <span className="text-5xl font-extrabold text-gray-900 tracking-tighter">
                    {isAnnual ? effectiveMonthlyPrice.toFixed(2).replace('.', ',') : monthlyPrice.toFixed(2).replace('.', ',')}
                  </span>
                  <span className="text-gray-500 text-sm font-medium">/mês</span>
                </div>
                {isAnnual && (
                  <p className="text-green-600 text-xs font-medium mt-2">
                    Cobrado R$ {finalAnnualPrice.toFixed(2).replace('.', ',')} anualmente
                  </p>
                )}
              </div>

            </div>

            {/* Benefícios e Ação */}
            <div className="p-8 md:p-12 bg-gray-50/30 flex flex-col lg:flex-row gap-12">
              
              {/* Lista de benefícios */}
              <div className="flex-1">
                <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-6">O que está incluso</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-4 gap-x-6">
                  {benefits.map((item, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-5 h-5 rounded-full bg-white border border-gray-200 flex items-center justify-center mt-0.5">
                        <FaCheck className="text-gray-900 w-2.5 h-2.5" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900 leading-tight">{item.feature}</p>
                        <p className="text-xs text-gray-500 mt-0.5">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Botões de Ação */}
              <div className="lg:w-72 flex flex-col gap-3 justify-center">
                <button
                  onClick={() => window.open(mercadoPagoUrl, '_blank')}
                  className="w-full bg-[#0a0a0a] text-white hover:bg-[#1a1a1a] font-medium py-3.5 rounded-xl transition-all duration-300 shadow-sm flex justify-center outline-none border border-transparent"
                >
                  <div className="flex items-center w-48">
                    <FaCreditCard className="text-lg opacity-80 w-6 flex-shrink-0 mr-2" />
                    <span className="text-left flex-1">Assinar Agora</span>
                  </div>
                </button>
                
                <button
                  onClick={() => window.open(whatsappUrl, '_blank')}
                  className="w-full bg-white text-gray-700 hover:bg-gray-50 hover:text-gray-900 border border-gray-200 font-medium py-3.5 rounded-xl transition-all duration-300 shadow-sm flex justify-center outline-none"
                >
                  <div className="flex items-center w-48">
                    <FaWhatsapp className="text-lg opacity-80 w-6 flex-shrink-0 mr-2" />
                    <span className="text-left flex-1">Falar com Consultor</span>
                  </div>
                </button>

                <p className="text-center text-xs text-gray-500 mt-2 font-light">
                  Cancele quando quiser.
                </p>

                {isAnnual && (
                  <button
                    onClick={() => setShowPlanoAnual(true)}
                    className="mt-2 text-xs text-gray-500 hover:text-gray-900 font-medium flex items-center justify-center gap-1 transition-colors"
                  >
                    Ver detalhes do desconto
                    <FaArrowRight className="w-3 h-3" />
                  </button>
                )}
              </div>
              
            </div>
          </div>

          {/* Trust Badges */}
          <div className="mt-8 flex flex-wrap justify-center gap-6 items-center">
             <div className="flex items-center gap-2 opacity-60 hover:opacity-100 transition-opacity grayscale hover:grayscale-0">
               <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>
               <span className="text-sm font-medium text-gray-600">Pagamento 100% Seguro</span>
             </div>
             <div className="flex items-center gap-2 opacity-60 hover:opacity-100 transition-opacity grayscale hover:grayscale-0">
               <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" /></svg>
               <span className="text-sm font-medium text-gray-600">Criptografia SSL</span>
             </div>
          </div>

        </div>
      </div>

      {showPlanoAnual && (
        <PlanoAnualDetails onClose={() => setShowPlanoAnual(false)} />
      )}
    </section>
  );
};

export default PricingCard;